/**
 * B2B Ex-VAT Price Display
 * Converts VAT-inclusive prices to ex-VAT display for business customers
 * when cart.attributes.customer_type === 'business'.
 *
 * Only changes the DISPLAYED price — never touches Shopify's actual prices or tax config.
 * Normal customers see no change at all.
 */
(function () {
  'use strict';

  var PRICING = window.__b2bPricing;
  if (!PRICING || !PRICING.enabled) return;

  // --- VAT rate resolution -------------------------------------------------
  var DEFAULT_VAT_RATE = 0.06; // Swedish reduced food VAT rate; auto-corrected from cart.tax_lines
  var vatRate = null;
  var rateReady = false;

  function resolveVatRate() {
    // 1. Liquid flag (cart.tax_lines available server-side when cart has items)
    if (PRICING.vatRate != null && !isNaN(parseFloat(PRICING.vatRate)) && parseFloat(PRICING.vatRate) > 0) {
      vatRate = parseFloat(PRICING.vatRate);
      try { sessionStorage.setItem('b2b_vat_rate_v2', String(vatRate)); } catch (e) {}
      rateReady = true;
      return Promise.resolve(vatRate);
    }

    // 2. sessionStorage (previously detected)
    try {
      var stored = sessionStorage.getItem('b2b_vat_rate_v2');
      if (stored) {
        vatRate = parseFloat(stored);
        if (vatRate > 0) {
          rateReady = true;
          // Still try to refresh from cart in background
          refreshRateFromCart();
          return Promise.resolve(vatRate);
        }
      }
    } catch (e) {}

    // 3. Fetch from /cart.js
    return refreshRateFromCart().then(function () {
      if (!vatRate) {
        vatRate = DEFAULT_VAT_RATE;
        try { sessionStorage.setItem('b2b_vat_rate_v2', String(vatRate)); } catch (e) {}
      }
      rateReady = true;
      return vatRate;
    });
  }

  function refreshRateFromCart() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (cart.tax_lines && cart.tax_lines.length > 0) {
          var rate = parseFloat(cart.tax_lines[0].rate);
          if (rate > 0 && rate !== vatRate) {
            vatRate = rate;
            try { sessionStorage.setItem('b2b_vat_rate_v2', String(vatRate)); } catch (e) {}
            // Rate changed — reset all converted elements and re-convert
            if (rateReady) {
              document.querySelectorAll('[data-b2b-done]').forEach(unmark);
              convertAll();
            }
          }
        }
      })
      .catch(function () {});
  }

  // --- Price formatting ----------------------------------------------------
  function formatSEK(amount) {
    // Swedish format: "839,62 kr"
    var s = amount.toLocaleString('sv-SE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return s + ' kr';
  }

  function parsePriceText(text) {
    if (!text) return null;
    // Match the first number group (handles "640.00 kr", "640,00 kr", "640 kr", "1 234,56 kr", etc.)
    var cleaned = text.replace(/[^\d.,\s]/g, '').trim();
    if (!cleaned) return null;
    // Remove spaces used as thousand separators
    cleaned = cleaned.replace(/\s/g, '');
    // Handle comma as decimal separator
    if (cleaned.indexOf(',') > -1 && cleaned.indexOf('.') > -1) {
      // Both present — assume comma is thousands, dot is decimal
      cleaned = cleaned.replace(/,/g, '');
    } else if (cleaned.indexOf(',') > -1) {
      // Only comma — Swedish decimal separator
      cleaned = cleaned.replace(',', '.');
    }
    var num = parseFloat(cleaned);
    return isNaN(num) ? null : num;
  }

  // --- Selectors -----------------------------------------------------------
  var SIMPLE_PRICE_SELECTORS = [
    '.fudge-product-card__price',
    '.fudge-b2b-card__price',
    '.cart-totals__original-value'
  ];

  var PRICE_SPAN_SELECTOR = '.price:not(.compare-at-price):not([class*="price-item--regular"])';
  var COMPARE_AT_SELECTOR = '.compare-at-price';
  var CART_LINE_PRICE_SELECTOR = '.cart-items__price text-component';
  var CART_TOTAL_SELECTOR = '.cart-totals__total-value';
  var CART_UNIT_PRICE_SELECTOR = '.cart-items__unit-price-wrapper span:not(.visually-hidden):not(.compare-at-price)';
  var STICKY_PRICE_SELECTOR = '.sticky-add-to-cart__price .price:not(.compare-at-price)';

  // --- Conversion ----------------------------------------------------------
  function isConverted(el) {
    return el.getAttribute('data-b2b-done') === '1';
  }

  function markConverted(el) {
    el.setAttribute('data-b2b-done', '1');
  }

  function unmark(el) {
    el.removeAttribute('data-b2b-done');
  }

  function buildB2BDisplay(el, incVat, originalText, bssAttrs) {
    var exVat = incVat / (1 + vatRate);
    el.setAttribute('data-b2b-original', originalText);
    el.textContent = '';
    el.classList.add('b2b-price-display');

    var main = document.createElement('span');
    main.className = 'b2b-price-exvat';
    main.textContent = formatSEK(exVat) + ' exkl. moms';

    var sub = document.createElement('span');
    sub.className = 'b2b-price-incvat';
    sub.textContent = formatSEK(incVat) + ' inkl. moms';

    // Re-apply BSS attributes to the main span so BSS can still target it
    if (bssAttrs && bssAttrs['bss-po-product-price'] !== undefined) {
      main.setAttribute('bss-po-product-price', '');
      if (bssAttrs['bss-po-product-id']) main.setAttribute('bss-po-product-id', bssAttrs['bss-po-product-id']);
      if (bssAttrs['bss-po-variant-id']) main.setAttribute('bss-po-variant-id', bssAttrs['bss-po-variant-id']);
    }

    el.appendChild(main);
    el.appendChild(sub);
    markConverted(el);
  }

  function convertSimplePrice(el) {
    if (isConverted(el)) return;
    var text = el.textContent.trim();
    if (!text) return;
    var incVat = parsePriceText(text);
    if (incVat == null || incVat <= 0) return;
    buildB2BDisplay(el, incVat, text, null);
  }

  function convertCompareAt(el) {
    if (isConverted(el)) return;
    var text = el.textContent.trim();
    if (!text) return;
    var incVat = parsePriceText(text);
    if (incVat == null || incVat <= 0) return;
    var exVat = incVat / (1 + vatRate);
    var out = formatSEK(exVat);
    el.setAttribute('data-b2b-original', text);
    el.textContent = out;
    el.setAttribute('data-b2b-converted', out);
    markConverted(el);
  }

  function convertPriceSpan(el) {
    if (isConverted(el)) return;
    var text = el.textContent.trim();
    if (!text) return;
    var incVat = parsePriceText(text);
    if (incVat == null || incVat <= 0) return;

    var bssAttrs = {};
    if (el.hasAttribute('bss-po-product-price')) bssAttrs['bss-po-product-price'] = '';
    if (el.hasAttribute('bss-po-product-id')) bssAttrs['bss-po-product-id'] = el.getAttribute('bss-po-product-id');
    if (el.hasAttribute('bss-po-variant-id')) bssAttrs['bss-po-variant-id'] = el.getAttribute('bss-po-variant-id');

    buildB2BDisplay(el, incVat, text, bssAttrs);
  }

  function convertTextComponent(el) {
    if (isConverted(el)) return;
    var text = el.textContent.trim();
    if (!text) return;
    var incVat = parsePriceText(text);
    if (incVat == null || incVat <= 0) return;
    if (el.hasAttribute('value')) {
      el.setAttribute('data-b2b-original-value', el.getAttribute('value'));
    }
    buildB2BDisplay(el, incVat, text, null);
  }

  function convertCartUnitPrice(el) {
    if (isConverted(el)) return;
    var text = el.textContent.trim();
    if (!text) return;
    var incVat = parsePriceText(text);
    if (incVat == null || incVat <= 0) return;
    var exVat = incVat / (1 + vatRate);
    var out = formatSEK(exVat);
    el.setAttribute('data-b2b-original', text);
    el.textContent = out;
    el.setAttribute('data-b2b-converted', out);
    markConverted(el);
  }

  // --- Batch conversion ----------------------------------------------------
  function convertAll() {
    if (!rateReady || !vatRate) return;

    document.querySelectorAll(SIMPLE_PRICE_SELECTORS.join(', ')).forEach(convertSimplePrice);

    document.querySelectorAll(PRICE_SPAN_SELECTOR).forEach(function (el) {
      if (el.closest('.visually-hidden')) return;
      convertPriceSpan(el);
    });

    document.querySelectorAll(STICKY_PRICE_SELECTOR).forEach(function (el) {
      if (el.closest('.visually-hidden')) return;
      convertPriceSpan(el);
    });

    document.querySelectorAll(COMPARE_AT_SELECTOR).forEach(function (el) {
      if (el.closest('.visually-hidden')) return;
      convertCompareAt(el);
    });

    document.querySelectorAll(CART_LINE_PRICE_SELECTOR).forEach(convertTextComponent);
    document.querySelectorAll(CART_TOTAL_SELECTOR).forEach(convertTextComponent);
    document.querySelectorAll(CART_UNIT_PRICE_SELECTOR).forEach(convertCartUnitPrice);

    // Cart discount values (negative amounts)
    document.querySelectorAll('.cart-discounts__value').forEach(function (el) {
      if (isConverted(el)) return;
      var text = el.textContent.trim();
      if (!text) return;
      var negative = text.charAt(0) === '-';
      var numText = negative ? text.substring(1) : text;
      var incVat = parsePriceText(numText);
      if (incVat == null || incVat <= 0) return;
      var exVat = incVat / (1 + vatRate);
      var out = (negative ? '-' : '') + formatSEK(exVat);
      el.setAttribute('data-b2b-original', text);
      el.textContent = out;
      el.setAttribute('data-b2b-converted', out);
      markConverted(el);
    });

    updateTaxInfo();
  }

  function updateTaxInfo() {
    var taxNote = document.querySelector('.cart-totals__tax-note small');
    if (taxNote && !taxNote.getAttribute('data-b2b-done')) {
      taxNote.setAttribute('data-b2b-done', '1');
      taxNote.textContent = 'Priser visas exkl. moms. Moms och frakt beräknas i kassan.';
    }
  }

  // --- Reconciliation for dynamic updates ----------------------------------
  function reconcile() {
    if (!rateReady || !vatRate) return;

    // Find previously-converted elements whose content was externally replaced
    document.querySelectorAll('[data-b2b-done]').forEach(function (el) {
      var exvatSpan = el.querySelector('.b2b-price-exvat');
      if (!exvatSpan) {
        // Text-replacement conversion (cart unit price, compare-at, discounts).
        // Only re-convert when the text was changed externally (server re-render,
        // idiomorph cart morph, BSS price update). Re-converting our own output
        // would divide the ex-VAT price by (1 + VAT) again on every pass, making
        // the displayed price count down towards 0.
        var convertedOut = el.getAttribute('data-b2b-converted');
        if (convertedOut !== null && el.textContent.trim() === convertedOut) return;
        unmark(el);
      } else {
        var exvatText = exvatSpan.textContent.trim();
        if (exvatText.indexOf('exkl. moms') === -1) {
          // BSS (or other JS) replaced the text inside .b2b-price-exvat with a raw price.
          // Extract the new incl-VAT price and re-convert in place.
          var newIncVat = parsePriceText(exvatText);
          if (newIncVat != null && newIncVat > 0) {
            var newExVat = newIncVat / (1 + vatRate);
            exvatSpan.textContent = formatSEK(newExVat) + ' exkl. moms';
            var incvatSpan = el.querySelector('.b2b-price-incvat');
            if (incvatSpan) {
              incvatSpan.textContent = formatSEK(newIncVat) + ' inkl. moms';
            }
            // Keep data-b2b-done; updated in place
          } else {
            // Can't parse — reset and try full conversion
            unmark(el);
          }
        }
      }
    });

    convertAll();
  }

  var reconcileTimer = null;
  function scheduleReconcile() {
    if (reconcileTimer) clearTimeout(reconcileTimer);
    reconcileTimer = setTimeout(reconcile, 150);
  }

  // --- MutationObserver ----------------------------------------------------
  var observer = null;
  function setupObserver() {
    if (observer) observer.disconnect();

    observer = new MutationObserver(function (mutations) {
      var needsReconcile = false;
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        if (m.type === 'childList' && m.addedNodes.length > 0) {
          needsReconcile = true;
          break;
        }
        if (m.type === 'characterData') {
          var target = m.target;
          if (target && target.parentNode && target.parentNode.closest) {
            // Check if this text change is inside a converted B2B price element
            var converted = target.parentNode.closest('[data-b2b-done]');
            if (converted) {
              needsReconcile = true;
              break;
            }
          }
        }
      }
      if (needsReconcile) scheduleReconcile();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true
    });
  }

  // --- CSS -----------------------------------------------------------------
  function injectStyles() {
    if (document.getElementById('b2b-pricing-styles')) return;
    var style = document.createElement('style');
    style.id = 'b2b-pricing-styles';
    style.textContent = [
      '.b2b-price-display {',
      '  display: inline-flex !important;',
      '  flex-direction: column;',
      '  line-height: 1.25;',
      '}',
      '.b2b-price-exvat {',
      '  font-weight: inherit;',
      '}',
      '.b2b-price-incvat {',
      '  font-size: 0.72em;',
      '  opacity: 0.6;',
      '  font-weight: 400;',
      '  margin-top: 1px;',
      '}',
      '.cart-items__price.b2b-price-display,',
      '.cart-totals__total-value.b2b-price-display {',
      '  align-items: flex-end;',
      '}',
      '.fudge-product-card__price.b2b-price-display,',
      '.fudge-b2b-card__price.b2b-price-display {',
      '  align-items: flex-start;',
      '}',
      '.price.b2b-price-display {',
      '  display: inline-flex !important;',
      '  flex-direction: column;',
      '}'
    ].join('\n');
    document.head.appendChild(style);
  }

  // --- Init ----------------------------------------------------------------
  function init() {
    injectStyles();
    resolveVatRate().then(function () {
      convertAll();
      setupObserver();

      document.addEventListener('cart:update', scheduleReconcile);
      window.addEventListener('pageshow', scheduleReconcile);

      // Safety-net periodic reconcile
      setInterval(function () {
        if (!document.hidden) reconcile();
      }, 3000);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
